import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { GlobalService } from '../../../../services/global.service';
import { NotificationsService } from '../../../common/notifications/notifications.service';
import { TranslocoService } from '@ngneat/transloco';
import { DOCUMENT } from '@angular/common';
import { PersonalizationService } from '../../../../services/personalization.service';
import { GAnalyticsService } from '../../../../services/g-analytics.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { ToastrService } from 'ngx-toastr';
import { Projects2Service } from '../../../../modules/projects/projects2.service';
import { Project360Service } from '../../../../modules/p-dashboard/project360.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Direction } from '@angular/cdk/bidi';
import { getHeaderLogoFull, getHeaderLogoIcon } from 'app/shared/utils/branded-placeholder.util';

@Component({
  selector: 'modern-layout',
  templateUrl: './modern.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class ModernLayoutComponent implements OnInit, OnDestroy {
  navigation: Navigation;
  isScreenSmall: boolean;
  dir: Direction = 'ltr';
  pageUrl: string;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private notificationsInterval: NodeJS.Timeout;
  arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

  /**
   * Constructor
   */
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private _project: Project360Service,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    public global: GlobalService,
    private _notifications: NotificationsService,
    private _translateService: TranslocoService,
    public _persona: PersonalizationService,
    private googleServices: GAnalyticsService,
    private _authService: AuthService,
    private _userService: UserService,
    private _toaster: ToastrService,
    private _projects2: Projects2Service,
    private _project360: Project360Service,
  ) {
    // language
    this.document.documentElement.lang = this._translateService.getActiveLang();
    if (this._translateService.getActiveLang() === 'ar') {
      this.document.documentElement.dir = 'rtl';
      this.document.body.dir = 'rtl';
      this.document.body.classList.add('rtl');
      this.dir = 'rtl';
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  /**
   * Light-mode header logo. The dark-mode header already reflects the
   * client's branding via PersonalizationService (_persona.logoFull/
   * logoIcon); these getters bring the light-mode logos in line with it
   * instead of always showing the hardcoded WhiteHelmet asset.
   */
  get lightHeaderLogoFull(): string {
    return getHeaderLogoFull(this._userService.isNwcClientSync());
  }

  get lightHeaderLogoIcon(): string {
    return getHeaderLogoIcon(this._userService.isNwcClientSync());
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('sm');
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    clearInterval(this.notificationsInterval);
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  routeHome(): void {
    this._project.isOperationEngineer()
      ? this._router.navigate(['/operation'])
      : this._router.navigate(['/projects']);
  }
}
